import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { Plus, Building2, Globe, ExternalLink, LayoutGrid, List } from 'lucide-react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useCompanies } from '@/stores/companies.store'
import { organizationStore } from '@/stores/organization.store'
import { Empty, EmptyMedia, EmptyDescription } from '@/components/ui/empty'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatTimestamp } from '@/lib/date-utils'
import { ActionGate } from '@/components/shared/action-gate'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/_app/_crm/companies/')({
  component: CompaniesPage,
})

function CompaniesPage() {
  useBreadcrumbFromRoute()
  
  const navigate = useNavigate()
  const [viewType, setViewType] = useState<'card' | 'table'>('card')
  const [includeArchived, setIncludeArchived] = useState(false)
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)

  const { companies, isLoading, isError, error } = useCompanies(undefined, { includeArchived })

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="flex items-center justify-end">
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="min-h-[400px]">
          <EmptyMedia variant="icon">
            <Building2 className="h-10 w-10" />
          </EmptyMedia>
          <EmptyDescription>
            Error loading companies: {error?.message || 'Unknown error'}
          </EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="rounded-lg border border-status-info-border bg-status-info-bg p-4 text-sm text-status-info-fg">
        <strong>CRM features are coming soon.</strong>{' '}
        Use this page to find and look up companies. Full CRM capabilities are in active development.
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Tabs value={viewType} onValueChange={(v) => setViewType(v as 'card' | 'table')}>
            <TabsList>
              <TabsTrigger value="card" className="gap-2">
                <LayoutGrid className="h-4 w-4" />
                Cards
              </TabsTrigger>
              <TabsTrigger value="table" className="gap-2">
                <List className="h-4 w-4" />
                Table
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-archived-companies"
              checked={includeArchived}
              onCheckedChange={(checked) => setIncludeArchived(checked === true)}
            />
            <Label htmlFor="show-archived-companies" className="text-sm font-normal">
              Show archived
            </Label>
          </div>
        </div>
        <ActionGate
          organizationId={selectedOrganizationId || ''}
          permission="ContactsManage"
          verb="create"
          noun="companies"
        >
          <Button onClick={() => navigate({ to: '/companies/new' })}>
            <Plus className="mr-2 h-4 w-4" />
            New Company
          </Button>
        </ActionGate>
      </div>

      {companies.length === 0 ? (
        <Empty className="min-h-[400px] border border-dashed rounded-lg">
          <EmptyMedia variant="icon">
            <Building2 className="h-10 w-10" />
          </EmptyMedia>
          <EmptyDescription>
            {includeArchived
              ? 'No active or archived companies to display.'
              : 'No companies yet. Create your first company to get started.'}
          </EmptyDescription>
        </Empty>
      ) : viewType === 'card' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((company) => (
            <Link
              key={company.id}
              to="/companies/$id"
              params={{ id: company.id }}
              className="block group"
            >
              <Card className="transition-colors hover:bg-muted/50">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium truncate">{company.name}</h3>
                          {company.archivedAt && <Badge variant="secondary">Archived</Badge>}
                        </div>
                        {company.websiteUrl && (
                          <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {company.websiteUrl.replace(/^https?:\/\//, '')}
                          </p>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground">
                    Created {company.createdAt ? formatTimestamp(company.createdAt, 'MMM d, yyyy') : '-'}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow
                  key={company.id}
                  className="cursor-pointer"
                  onClick={() => navigate({ to: '/companies/$id', params: { id: company.id } })}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium">{company.name}</span>
                      {company.archivedAt && <Badge variant="secondary">Archived</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {company.websiteUrl ? (
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {company.websiteUrl.replace(/^https?:\/\//, '')}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {company.createdAt ? formatTimestamp(company.createdAt, 'MMM d, yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
